import React, { useState, useEffect, useRef } from 'react';
import './DetailForm.css';
import SmartDropdown from './SmartDropdown';
import ColorField from './ColorField';

const DetailForm = ({ fields, scenario, onGenerate, onSkip, initialFormData }) => {
  const [formData, setFormData] = useState({});
  const hasInitializedRef = useRef(false);

  // Only run once on mount to initialize form data from initialFormData (if provided)
  useEffect(() => {
    if (hasInitializedRef.current) return;
    if (!fields || fields.length === 0) return;
    
    hasInitializedRef.current = true;
    
    // If no initialFormData provided, just start with empty form
    if (!initialFormData || Object.keys(initialFormData).length === 0) {
      return;
    }

    const fieldMap = new Map(fields.map(f => [f.id, f]));
    
    const toCamelCase = (str) => {
      if (!str) return '';
      return str
        .replace(/\s+(.)/g, (_, c) => c.toUpperCase())
        .replace(/\s/g, '')
        .replace(/^(.)/, (_, c) => c.toLowerCase());
    };
    
    const filteredFormData = {};
    
    Object.entries(initialFormData).forEach(([key, value]) => {
      if (key === 'assets') {
        filteredFormData.assets = value;
        return;
      }
      
      if (key === 'dynamicInput') return;
      if (fieldMap.has(key)) {
        filteredFormData[key] = value;
      }
    });
    
    if (initialFormData.dynamicInput && typeof initialFormData.dynamicInput === 'object') {
      Object.entries(initialFormData.dynamicInput).forEach(([key, value]) => {
        const matchingField = fields.find(f => {
          return f.id === key || (f.display && toCamelCase(f.display) === key);
        });
        
        if (matchingField && !filteredFormData[matchingField.id]) {
          filteredFormData[matchingField.id] = value;
        }
      });
    }
    
    setFormData(filteredFormData);
  }, [initialFormData, fields]);
  
  const [hasDetails, setHasDetails] = useState(false);

  // Check if any field has a value
  useEffect(() => {
    const isFilled = Object.values(formData).some(val => {
      if (Array.isArray(val)) return val.length > 0;
      return val && typeof val === 'string' && val.trim().length > 0;
    });
    setHasDetails(isFilled);
  }, [formData]);

  const arrayFields = ['for', 'color', 'typography', 'size', 'mood', 'layout', 'material'];
  
  const handleChange = (e) => {
    const { name, value, files, type } = e.target;

    if (type === 'file') {
      const newFiles = files ? Array.from(files) : [];
      setFormData(prev => ({ ...prev, [name]: [...(prev[name] || []), ...newFiles] }));
      return;
    }

    if (arrayFields.includes(name)) {
      const arrayValue = Array.isArray(value) 
        ? value 
        : (value ? value.split(',').map(v => v.trim()).filter(Boolean) : []);
      setFormData(prev => ({ ...prev, [name]: arrayValue }));
      return;
    }

    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Remove a specific file from assets
  const handleRemoveFile = (fieldName, indexToRemove) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: prev[fieldName].filter((_, index) => index !== indexToRemove)
    }));
  };

  // Get preview URL for a file or URL string
  const getFilePreviewUrl = (file) => {
    if (file instanceof File) {
      return URL.createObjectURL(file);
    }
    if (typeof file === 'string') {
      return file;
    }
    return null;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (hasDetails) {
      onGenerate(formData);
    } else {
      // Even if skipping, we can pass empty formData or trigger skip handler
      // Using onGenerate({}) is cleaner as it keeps flow consistent
      onGenerate({});
    }
  };

  return (
    <div className="generation-form-container">
      <div className="initial-prompt-header">
        <img src="/KURA-logo.png" alt="KURA Logo" className="kura-logo" />
      </div>

      <h2 className="form-title">Add details to make it personal</h2>
      <p className="form-hint">All fields are optional — fill in as much or as little as you'd like</p>

      <form onSubmit={handleSubmit} className="generation-form">
        <div className="form-scroll-container">
          {fields.map((field) => (
            <div key={field.id} className="form-group">
              <label htmlFor={field.id}>{field.display || field.label}</label>

              {/* Color type - Color picker + text input */}
              {field.type === 'color' ? (
                <ColorField
                  id={field.id}
                  display={field.display}
                  placeholder={field.placeholder}
                  value={formData[field.id] || []}
                  onChange={handleChange}
                />
              ) : /* Smart Dropdown - Dropdown with custom input (also handles regular dropdown) */
                field.type === 'smart_dropdown' || field.type === 'dropdown' || field.type === 'select' ? (
                  <SmartDropdown
                    id={field.id}
                    display={field.display}
                    options={field.options || []}
                    placeholder={field.placeholder || 'Select or type...'}
                    value={Array.isArray(formData[field.id]) 
                      ? formData[field.id].join(', ') 
                      : (formData[field.id] || '')}
                    onChange={handleChange}
                  />
                ) : /* File upload */
                  field.type === 'file' ? (
                    <div className={`file-upload-wrapper ${formData[field.id]?.length > 0 ? 'has-files' : ''}`}>
                      <input
                        type="file"
                        id={field.id}
                        name={field.id}
                        onChange={handleChange}
                        className="file-input"
                        multiple={true}
                        accept="image/*"
                      />
                      <div className="file-custom-trigger">
                        {formData[field.id] && formData[field.id].length > 0
                          ? `${formData[field.id].length} file(s) selected`
                          : field.placeholder || "Choose files..."}
                      </div>
                    </div>
                  ) : /* Default: Text input */
                    (
                      <input
                        type="text"
                        id={field.id}
                        name={field.id}
                        value={Array.isArray(formData[field.id])
                          ? formData[field.id].join(', ')
                          : (formData[field.id] || '')}
                        onChange={handleChange}
                        placeholder={field.placeholder}
                        className="form-input"
                      />
                    )}
            </div>
          ))}

          {/* Static Asset Upload Field */}
          <div className="form-group asset-upload-group">
            <label htmlFor="assets">Optional Asset Images</label>

            {/* Preview grid for uploaded assets */}
            {formData.assets && formData.assets.length > 0 && (
              <div className="detail-form-asset-grid">
                {formData.assets.map((file, index) => (
                  <div key={index} className="detail-form-asset-item">
                    <img
                      src={getFilePreviewUrl(file)}
                      alt={`Asset ${index + 1}`}
                      className="detail-form-asset-image"
                    />
                    <button
                      type="button"
                      className="detail-form-asset-remove"
                      onClick={() => handleRemoveFile('assets', index)}
                      title="Remove"
                    >
                      ×
                    </button>
                    <span className="detail-form-asset-name">
                      {file instanceof File ? file.name : 'Asset Image'}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Upload trigger */}
            <div className={`file-upload-wrapper ${formData.assets?.length > 0 ? 'has-files' : ''}`}>
              <input
                type="file"
                id="assets"
                name="assets"
                onChange={handleChange}
                className="file-input"
                multiple
                accept="image/*"
              />
              <div className="file-custom-trigger asset-trigger">
                <span className="upload-icon">📎</span>
                {formData.assets && formData.assets.length > 0
                  ? "Add more images..."
                  : "Upload inspiration images, logos, brand assets..."}
              </div>
            </div>
          </div>
        </div>

        <div className="form-actions" style={{ marginTop: '2rem', justifyContent: 'center' }}>
          <button
            type="submit"
            className="submit-button"
            style={{ width: '100%', maxWidth: '400px' }}
          >
            {hasDetails ? "Generate with my details" : "Skip details, just inspire me"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default DetailForm;
